import api from "./api";

export interface SuperAdminDashboardData {
  companies: {
    total: number;
    active: number;
    inactive: number;
    unverified: number;
  };
  cards: {
    active_companies: number;
    inactive_companies: number;
    total_categories: number;
    total_customers: number;
    total_commission: number;
    total_sales: number;
  };
  range: {
    start_date: string;
    end_date: string;
  };
  users: {
    total: number;
    super_admins: number;
    agents: number;
  };
  packages: {
    total: number;
    active: number;
  };
  revenue: {
    total_amount: number;
    total_commission: number;
    monthly: Array<{
      month: number;
      amount: number;
      commission: number;
      net: number;
    }>;
  };
}

export interface SuperAdminReportData {
  range: {
    start_date: string;
    end_date: string;
  };
  companies: {
    total: number;
    active: number;
    deactive: number;
    growth: Array<{
      date: string;
      total: number;
    }>;
  };
  payments: {
    total_amount: number;
    total_commission: number;
    net_income: number;
  };
  bookings: {
    total: number;
    completed: number;
    canceled: number;
    online: number;
    pos: number;
  };
}

export interface SuperAdminLoginLog {
  id: number;
  user_id?: number | null;
  company_id?: number | null;
  token_id?: number | null;
  email_attempted?: string | null;
  event: string;
  ip_address?: string | null;
  user_agent?: string | null;
  route?: string | null;
  method?: string | null;
  description?: string | null;
  created_at?: string;
  user?: {
    id: number;
    name?: string | null;
    email?: string | null;
    company_id?: number | null;
  } | null;
  company?: {
    id: number;
    company_name?: string | null;
  } | null;
}

export interface SuperAdminUserActivityLog {
  id: number;
  actor_user_id: number;
  company_id?: number | null;
  module: string;
  action: string;
  entity_type?: string | null;
  entity_id?: number | null;
  request_data?: Record<string, any> | null;
  meta?: Record<string, any> | null;
  route?: string | null;
  method?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  description?: string | null;
  created_at?: string;
  user?: {
    id: number;
    name?: string | null;
    email?: string | null;
    company_id?: number | null;
  } | null;
  company?: {
    id: number;
    company_name?: string | null;
  } | null;
}

export interface PaginatedResponse<T> {
  current_page: number;
  data: T[];
  last_page: number;
  per_page: number;
  total: number;
}

export interface SuperAdminCompanyBilling {
  subscription_status: string | null;
  subscription_id: number | null;
  package_name: string | null;
  billing_cycle: string | null;
  amount_cents: number | null;
  currency: string;
  has_paid: boolean;
  has_payment_method: boolean;
  payment_method: {
    brand: string | null;
    last4: string | null;
  };
  next_charge_at: string | null;
  last_payment_at: string | null;
  last_payment_amount_cents: number | null;
  company_status: string;
}

export interface SuperAdminCompany {
  id: number;
  company_name: string;
  company_email: string;
  company_phone: string;
  xecrm_support_id?: string | null;
  xecrm_company_id?: string | null;
  logo_url?: string;
  slug?: string | null;
  is_claim?: string | null;
  website?: string | null;
  address?: string | null;
  timezone?: string | null;
  date_format?: string | null;
  time_format?: string | null;
  locale?: string | null;
  website_status?: string | null;
  subdomain?: string | null;
  custom_domain?: string | null;
  status: string;
  verified: string;
  package_id: number | null;
  package?: {
    id: number;
    name: string;
    status?: string;
    max_employees?: number;
    max_services?: number;
    max_products?: number;
    max_customers?: number;
    max_deals?: number;
    max_roles?: number;
  } | null;
  currency?: {
    id: number;
    currency_name?: string;
    currency_code?: string;
    currency_symbol?: string;
  } | null;
  owner?: {
    id: number;
    name?: string;
    email?: string;
    mobile?: string;
    calling_code?: string;
  } | null;
  user_count?: number;
  bookings_count?: number;
  categories_count?: number;
  services_count?: number;
  trial_ends_at?: string | null;
  licence_expire_on?: string | null;
  created_at: string;
  billing?: SuperAdminCompanyBilling | null;
}

export interface SuperAdminPackage {
  id: number;
  name: string;
  monthly_price: number;
  annual_price: number;
  status: string;
  max_employees: number;
  max_services: number;
  max_products?: number;
  max_customers?: number;
  max_deals?: number;
  max_roles?: number;
  no_of_days?: number;
  stripe_monthly_plan_id?: string | null;
  stripe_annual_plan_id?: string | null;
  razorpay_monthly_plan_id?: string | null;
  razorpay_annual_plan_id?: string | null;
  description?: string | null;
  type?: string | null;
  currency_id?: number | null;
  make_private?: "yes" | "no" | "true" | "false" | null;
  mark_recommended?: "yes" | "no" | "true" | "false" | null;
  package_modules?: string | string[] | null;
  created_at?: string;
}

export interface SuperAdminPackageModuleOption {
  id: number;
  name: string;
}

export interface SuperAdminOfflinePaymentMethod {
  id: number;
  name: string;
}

export interface SuperAdminCoupon {
  id: number;
  title: string;
  code: string | null;
  discount_type: string;
  amount: number;
  status: string;
  start_date_time: string | null;
  end_date_time: string | null;
  uses_limit?: number | null;
  used_time?: number | null;
  minimum_purchase_amount?: number | null;
  description?: string | null;
  days?: string | string[] | null;
  percent?: number | null;
  created_at?: string;
}

export interface SuperAdminCategory {
  id: number;
  name: string;
  status: string;
  services_count?: number;
  color_code?: string | null;
  created_at?: string;
}

export interface SuperAdminClaimBusiness {
  id: number;
  company_name: string;
  company_email?: string | null;
  address?: string | null;
  is_claim?: string | null;
  created_at?: string;
}

export interface SuperAdminCompanyClaim {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  message?: string | null;
  status: string;
  created_at?: string;
  company?: {
    id: number;
    company_name?: string | null;
    address?: string | null;
  } | null;
}

export interface SuperAdminCurrencyOption {
  id: number;
  currency_name?: string;
  currency_code?: string;
  currency_symbol?: string;
}

export interface SuperAdminLanguageOption {
  id: number;
  language_name?: string;
  language_code?: string;
  status?: string;
}

export interface SuperAdminSettingsResponse {
  setting: Record<string, any>;
  currencies: SuperAdminCurrencyOption[];
  languages: SuperAdminLanguageOption[];
}

export interface SuperAdminInvoiceSummary {
  subscription_total: number;
}

export interface SuperAdminSubscriptionInvoice {
  id: number;
  invoice_id: string;
  company: string;
  package: string;
  transaction_id: string;
  method: string;
  method_key: string;
  amount: number;
  paid_on?: string | null;
  next_pay_date?: string | null;
  download_url?: string | null;
}

export interface SuperAdminBookingInvoice {
  id: number;
  company: string;
  transaction_id: string;
  amount: number;
  application_fee: number;
  method: string;
  paid_on?: string | null;
  currency_symbol?: string | null;
}

export interface SuperAdminOfflineInvoice {
  id: number;
  company: string;
  company_name: string;
  total_earning: number;
  commission_amount: number;
  paid_amount: number;
  pending_amount: number;
  paid_on?: string | null;
  status: string;
  status_label: string;
  currency_symbol?: string | null;
}

export interface SuperAdminOfflinePlanRequest {
  id: number;
  company?: {
    id?: number;
    company_name?: string | null;
  } | null;
  package?: {
    id?: number;
    name?: string | null;
  } | null;
  offline_method?: {
    id?: number;
    name?: string | null;
  } | null;
  status: "pending" | "verified" | "rejected" | string;
  description?: string | null;
  file?: string | null;
  file_name?: string | null;
  created_at?: string | null;
}

export interface SuperAdminLocationCountry {
  id: number;
  name: string;
  phonecode: number | string;
}

export interface SuperAdminLocationTimezone {
  id: number;
  zone_name: string;
  country_id?: number;
}

export interface SuperAdminLocation {
  id: number;
  name: string;
  lat: number;
  lng: number;
  country_id: number;
  timezone_id: number;
  status: "active" | "inactive" | string;
  country?: {
    id: number;
    name?: string;
    phonecode?: number;
  } | null;
  timezone?: {
    id: number;
    zone_name?: string;
    country_id?: number;
  } | null;
  created_at?: string;
  updated_at?: string;
}

const unwrapData = <T>(response: any): T => response?.data?.data ?? response?.data;

export const getSuperAdminDashboard = async (
  startDate?: string,
  endDate?: string
): Promise<SuperAdminDashboardData> => {
  const params: Record<string, string> = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get("/super-admin/dashboard", { params });
  return unwrapData<SuperAdminDashboardData>(response);
};

export const getSuperAdminReport = async (
  startDate?: string,
  endDate?: string
): Promise<SuperAdminReportData> => {
  const params: Record<string, string> = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  const response = await api.get("/super-admin/reports", { params });
  return unwrapData<SuperAdminReportData>(response);
};

export const getSuperAdminLoginLogs = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminLoginLog>> => {
  const response = await api.get("/super-admin/activity/login-logs", { params });
  return unwrapData<PaginatedResponse<SuperAdminLoginLog>>(response);
};

export const getSuperAdminUserActivityLogs = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminUserActivityLog>> => {
  const response = await api.get("/super-admin/activity/user-logs", { params });
  return unwrapData<PaginatedResponse<SuperAdminUserActivityLog>>(response);
};

export const getSuperAdminCompanies = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminCompany>> => {
  const response = await api.get("/super-admin/companies", { params });
  return unwrapData<PaginatedResponse<SuperAdminCompany>>(response);
};

export const getSuperAdminCompany = async (id: number): Promise<SuperAdminCompany> => {
  const response = await api.get(`/super-admin/companies/${id}`);
  return unwrapData<SuperAdminCompany>(response);
};

export const createSuperAdminCompany = async (
  payload: Record<string, any>
): Promise<SuperAdminCompany> => {
  const response = await api.post("/super-admin/companies", payload, {
    headers: payload instanceof FormData ? { "Content-Type": "multipart/form-data" } : undefined,
  });
  return unwrapData<SuperAdminCompany>(response);
};

export const uploadSuperAdminCompaniesBulk = async (file: File): Promise<Record<string, any>> => {
  const formData = new FormData();
  formData.append("bulk_businesses_file", file);
  const response = await api.post("/super-admin/companies/bulk-upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return unwrapData<Record<string, any>>(response);
};

export const updateSuperAdminCompany = async (
  id: number,
  payload: Record<string, any>
): Promise<SuperAdminCompany> => {
  const response =
    payload instanceof FormData
      ? await api.post(`/super-admin/companies/${id}`, (() => {
          if (!payload.has("_method")) {
            payload.append("_method", "PUT");
          }
          return payload;
        })(), {
          headers: { "Content-Type": "multipart/form-data" },
        })
      : await api.put(`/super-admin/companies/${id}`, payload);
  return unwrapData<SuperAdminCompany>(response);
};

export const deleteSuperAdminCompany = async (id: number): Promise<Record<string, any>> => {
  const response = await api.delete(`/super-admin/companies/${id}`);
  return unwrapData<Record<string, any>>(response);
};

export const changeSuperAdminCompanyPackage = async (
  payload: Record<string, any>
): Promise<Record<string, any>> => {
  const response = await api.post("/super-admin/companies/change-package", payload);
  return unwrapData<Record<string, any>>(response);
};

export const getSuperAdminCompanyPaymentMethods = async (): Promise<SuperAdminOfflinePaymentMethod[]> => {
  const response = await api.get("/super-admin/companies/payment-methods");
  return unwrapData<SuperAdminOfflinePaymentMethod[]>(response);
};

export const loginAsVendor = async (
  companyId: number
): Promise<Record<string, any>> => {
  const response = await api.post(`/super-admin/companies/${companyId}/login-as-vendor`);
  return unwrapData<Record<string, any>>(response);
};

export const scrapeSuperAdminCompany = async (
  payload: { company_id: number; booksy_link: string }
): Promise<Record<string, any>> => {
  const response = await api.post("/super-admin/companies/scrap", payload);
  return unwrapData<Record<string, any>>(response);
};

export const importSuperAdminCompanyCustomers = async (
  companyId: number,
  file: File
): Promise<{ company_id: number; imported: number; duplicates: number }> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post(
    `/super-admin/companies/${companyId}/import-customers`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return unwrapData<{ company_id: number; imported: number; duplicates: number }>(
    response
  );
};

/** Public sample CSV on API origin (not under /api). */
export const SUPERADMIN_CUSTOMERS_CSV_SAMPLE_URL =
  "https://salonify.co.uk/csv-samples/customers.csv";

export const getSuperAdminPackages = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminPackage>> => {
  const response = await api.get("/super-admin/packages", { params });
  return unwrapData<PaginatedResponse<SuperAdminPackage>>(response);
};

export const getSuperAdminPackage = async (id: number): Promise<SuperAdminPackage> => {
  const response = await api.get(`/super-admin/packages/${id}`);
  return unwrapData<SuperAdminPackage>(response);
};

export const createSuperAdminPackage = async (
  payload: Record<string, any>
): Promise<SuperAdminPackage> => {
  const response = await api.post("/super-admin/packages", payload);
  return unwrapData<SuperAdminPackage>(response);
};

export const updateSuperAdminPackage = async (
  id: number,
  payload: Record<string, any>
): Promise<SuperAdminPackage> => {
  const response = await api.put(`/super-admin/packages/${id}`, payload);
  return unwrapData<SuperAdminPackage>(response);
};

export const deleteSuperAdminPackage = async (id: number): Promise<Record<string, any>> => {
  const response = await api.delete(`/super-admin/packages/${id}`);
  return unwrapData<Record<string, any>>(response);
};

export const getSuperAdminPackageModules = async (): Promise<SuperAdminPackageModuleOption[]> => {
  const response = await api.get("/super-admin/packages/modules");
  return unwrapData<SuperAdminPackageModuleOption[]>(response);
};

export const getSuperAdminCoupons = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminCoupon>> => {
  const response = await api.get("/super-admin/coupons", { params });
  return unwrapData<PaginatedResponse<SuperAdminCoupon>>(response);
};

export const getSuperAdminCoupon = async (id: number): Promise<SuperAdminCoupon> => {
  const response = await api.get(`/super-admin/coupons/${id}`);
  return unwrapData<SuperAdminCoupon>(response);
};

export const createSuperAdminCoupon = async (
  payload: Record<string, any>
): Promise<SuperAdminCoupon> => {
  const response = await api.post("/super-admin/coupons", payload);
  return unwrapData<SuperAdminCoupon>(response);
};

export const updateSuperAdminCoupon = async (
  id: number,
  payload: Record<string, any>
): Promise<SuperAdminCoupon> => {
  const response = await api.put(`/super-admin/coupons/${id}`, payload);
  return unwrapData<SuperAdminCoupon>(response);
};

export const deleteSuperAdminCoupon = async (id: number): Promise<Record<string, any>> => {
  const response = await api.delete(`/super-admin/coupons/${id}`);
  return unwrapData<Record<string, any>>(response);
};

export const getSuperAdminCategories = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminCategory>> => {
  const response = await api.get("/super-admin/categories", { params });
  return unwrapData<PaginatedResponse<SuperAdminCategory>>(response);
};

export const getSuperAdminClaimBusinesses = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminClaimBusiness>> => {
  const response = await api.get("/super-admin/companies/claimed-businesses", { params });
  return unwrapData<PaginatedResponse<SuperAdminClaimBusiness>>(response);
};

export const getSuperAdminCompanyClaims = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminCompanyClaim>> => {
  const response = await api.get("/super-admin/companies/claims", { params });
  return unwrapData<PaginatedResponse<SuperAdminCompanyClaim>>(response);
};

export const getSuperAdminSettings = async (): Promise<SuperAdminSettingsResponse> => {
  const response = await api.get("/super-admin/settings");
  return unwrapData<SuperAdminSettingsResponse>(response);
};

export const getSuperAdminInvoiceSummary = async (): Promise<SuperAdminInvoiceSummary> => {
  const response = await api.get("/super-admin/invoices/summary");
  return unwrapData<SuperAdminInvoiceSummary>(response);
};

export const getSuperAdminSubscriptionInvoices = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminSubscriptionInvoice>> => {
  const response = await api.get("/super-admin/invoices/subscription", { params });
  return unwrapData<PaginatedResponse<SuperAdminSubscriptionInvoice>>(response);
};

export const getSuperAdminBookingInvoices = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminBookingInvoice>> => {
  const response = await api.get("/super-admin/invoices/bookings", { params });
  return unwrapData<PaginatedResponse<SuperAdminBookingInvoice>>(response);
};

export const getSuperAdminOfflineInvoices = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminOfflineInvoice>> => {
  const response = await api.get("/super-admin/invoices/offline", { params });
  return unwrapData<PaginatedResponse<SuperAdminOfflineInvoice>>(response);
};

export const updateSuperAdminOfflineInvoice = async (
  id: number,
  payload: { paid_amount: number; paid_on: string; status: "pending" | "settled" }
): Promise<Record<string, any>> => {
  const response = await api.put(`/super-admin/invoices/offline/${id}`, payload);
  return unwrapData<Record<string, any>>(response);
};

export const getSuperAdminOfflinePlanRequests = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminOfflinePlanRequest>> => {
  const response = await api.get("/super-admin/offline-plan-requests", { params });
  return unwrapData<PaginatedResponse<SuperAdminOfflinePlanRequest>>(response);
};

export const verifySuperAdminOfflinePlanRequest = async (id: number): Promise<Record<string, any>> => {
  const response = await api.post("/super-admin/offline-plan-requests/verify", { id });
  return unwrapData<Record<string, any>>(response);
};

export const rejectSuperAdminOfflinePlanRequest = async (id: number): Promise<Record<string, any>> => {
  const response = await api.post("/super-admin/offline-plan-requests/reject", { id });
  return unwrapData<Record<string, any>>(response);
};

export const getSuperAdminLocations = async (
  params: Record<string, any> = {}
): Promise<PaginatedResponse<SuperAdminLocation>> => {
  const response = await api.get("/super-admin/locations", { params });
  return unwrapData<PaginatedResponse<SuperAdminLocation>>(response);
};

export const getSuperAdminLocation = async (id: number): Promise<SuperAdminLocation> => {
  const response = await api.get(`/super-admin/locations/${id}`);
  return unwrapData<SuperAdminLocation>(response);
};

export const createSuperAdminLocation = async (payload: {
  name: string;
  country_id: number;
  timezone_id: number;
  lat: number;
  lng: number;
}): Promise<SuperAdminLocation> => {
  const response = await api.post("/super-admin/locations", payload);
  return unwrapData<SuperAdminLocation>(response);
};

export const updateSuperAdminLocation = async (
  id: number,
  payload: {
    name: string;
    country_id: number;
    timezone_id: number;
    lat: number;
    lng: number;
  }
): Promise<SuperAdminLocation> => {
  const response = await api.put(`/super-admin/locations/${id}`, payload);
  return unwrapData<SuperAdminLocation>(response);
};

export const deleteSuperAdminLocation = async (id: number): Promise<Record<string, any>> => {
  const response = await api.delete(`/super-admin/locations/${id}`);
  return unwrapData<Record<string, any>>(response);
};

export const changeSuperAdminLocationStatus = async (
  locationId: number,
  locationStatus: "active" | "inactive"
): Promise<SuperAdminLocation> => {
  const response = await api.post("/super-admin/locations/change-status", {
    location_id: locationId,
    location_status: locationStatus,
  });
  return unwrapData<SuperAdminLocation>(response);
};

export const getSuperAdminLocationCountries = async (): Promise<SuperAdminLocationCountry[]> => {
  const response = await api.get("/super-admin/locations/countries");
  return unwrapData<SuperAdminLocationCountry[]>(response);
};

export const getSuperAdminLocationTimezones = async (
  countryId: number
): Promise<SuperAdminLocationTimezone[]> => {
  const response = await api.get("/super-admin/locations/timezones", { params: { country_id: countryId } });
  return unwrapData<SuperAdminLocationTimezone[]>(response);
};

export const updateSuperAdminSettings = async (
  payload: Record<string, any>
): Promise<Record<string, any>> => {
  const response = await api.put("/super-admin/settings", payload);
  return unwrapData<Record<string, any>>(response);
};

